import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { push } from 'connected-react-router';
import { OrderedMap, Set } from 'immutable';

import getPaymentMethods from 'action-creators/payment-methods/get-payment-methods';
import setDefaultPaymentMethod from 'action-creators/payment-methods/set-default-payment-method';
import deletePaymentMethod from 'action-creators/payment-methods/delete-payment-method';

import valid from 'card-validator';

import PaymentMethodForm from 'components/payment-methods/payment-method-form';
import BusinessProfileTimeline from 'components/payment-methods/business-profiles/timeline';
import ProgressButton from 'components/common/buttons/progress-button';
import EmailInput from 'components/payment-methods/business-profiles/email-input';

import PaymentMethodModel from 'models/payment-method';
import NewPaymentMethodModel from 'models/braintree/new-payment-method';
import UserModel from 'models/user';

import saveBusinessProfileCreator from 'action-creators/payment-methods/save-business-profile';
import modifyRequiredFieldListCreator from 'action-creators/payment-methods/modify-required-field-list';
import setShouldScrollToErrorCreator from 'action-creators/payment-methods/set-should-scroll-to-error';
import braintreeFieldStateChange from 'action-creators/payment-methods/braintree-field-state-change';
import trackEventCreator from 'action-creators/analytics/track-event';

import Brand from 'models/brand';
import AccountHeadline from 'components/common/account/account-headline';

const propTypes = {
  paymentMethod: PropTypes.oneOfType([
    PropTypes.instanceOf(PaymentMethodModel),
    PropTypes.instanceOf(NewPaymentMethodModel),
  ]),
  paymentMethods: PropTypes.instanceOf(OrderedMap).isRequired,
  selectedPaymentMethodId: PropTypes.string,
  defaultPaymentMethodId: PropTypes.string,
  getPaymentMethods: PropTypes.func.isRequired,
  setDefaultPaymentMethod: PropTypes.func.isRequired,
  deletePaymentMethod: PropTypes.func.isRequired,
  requiredFields: PropTypes.instanceOf(Set).isRequired,
  requiredFieldErrors: PropTypes.instanceOf(OrderedMap).isRequired,
  shouldScrollToError: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  saveBusinessProfile: PropTypes.func.isRequired,
  braintreeFieldStateChange: PropTypes.func.isRequired,
  setShouldScrollToError: PropTypes.func.isRequired,
  modifyRequiredFieldList: PropTypes.func.isRequired,
  dismissPage: PropTypes.func.isRequired,
  user: PropTypes.instanceOf(UserModel).isRequired,
  app: PropTypes.string.isRequired,
  trackEvent: PropTypes.func.isRequired,

  // Optional, or async loaded properties
  braintreeToken: PropTypes.string,
  handleBraintreePaymentMethodError: PropTypes.func,
  brand: PropTypes.instanceOf(Brand).isRequired,
};

const defaultProps = {
  selectedPaymentMethodId: null,
  defaultPaymentMethodId: null,
  paymentMethod: new NewPaymentMethodModel(),
  braintreeToken: null,
  handleBraintreePaymentMethodError: () => {},
};

class EditPaymentMethod extends Component {
  editPaymentMethod() {
    const { paymentMethod } = this.props;
    return push(paymentMethod.editURL);
  }

  constructor(props) {
    super(props);

    this.state = {
      dirty: false,
      cardValues: {
        number: null,
        expirationMonth: null,
        expirationYear: null,
        cvv: null,
        postalCode: null,
      },
      emailValue: '',
    };

    this.dismissPage = this.dismissPage.bind(this);
    this.onFieldEvent = this.onFieldEvent.bind(this);
    this.onSubmit = this.onSubmit.bind(this);
  }

  componentDidMount() {
    require('smoothscroll-polyfill').polyfill();
    window.scroll({ top: 0, left: 0, behavior: 'smooth' });
    const { braintreeToken } = this.props;
    if (!braintreeToken) { return; }
    import('braintree-web').then(braintree => this.initializeBraintree(braintree)).catch(e => console.error(`Failed to initialize braintree: ${e}`));
  }

  componentDidUpdate(prevProps) {
    const { braintreeToken } = this.props;
    if (braintreeToken && !prevProps.braintreeToken) {
      import('braintree-web').then(braintree => this.initializeBraintree(braintree)).catch(e => console.error(`Failed to initialize braintree: ${e}`));
    }
  }

  componentWillUnmount() {
    if (this.btHandler) {
      this.btHandler.teardown(() => {
        this.btHandler = null;
      });
    }
  }

  onSubmit(e) {
    e.preventDefault();
    e.stopPropagation();

    const { saveBusinessProfile, selectedPaymentMethodId } = this.props;
    const { cardValues } = this.state;

    if (this.state.dirty) {
      this.client.request({
        endpoint: 'payment_methods/credit_cards',
        method: 'post',
        data: {
          creditCard: {
            number: cardValues.number,
            cvv: cardValues.cvv,
            expirationMonth: cardValues.expirationMonth,
            expirationYear: cardValues.expirationYear,
            billingAddress: {
              postalCode: cardValues.postalCode,
            },
            options: {
              validate: false,
            },
          },
        },
      }, (requestErr, response) => {
        if (requestErr) {
          console.warn('err', requestErr);
        } else {
          const emailField = document.querySelector('#profile-email');
          saveBusinessProfile({ id: selectedPaymentMethodId, nonce: response.creditCards[0].nonce, email: emailField.value, isUpdate: true });
        }
      });
    } else {
      const emailField = document.querySelector('#profile-email');
      saveBusinessProfile({ id: selectedPaymentMethodId, nonce: null, email: emailField.value, isUpdate: true });
    }
  }

  getExistingPaymentMethod() {
    const { paymentMethods, selectedPaymentMethodId } = this.props;
    let existingPaymentMethod = null;
    if (paymentMethods && paymentMethods.size) {
      existingPaymentMethod = paymentMethods.get(selectedPaymentMethodId);
    }
    return existingPaymentMethod;
  }

  onFieldEvent(e) {
    // For edit page only, e.target.dataFieldName will be provided on BT field receiving pre-fill prop
    const prepopulatedFieldName = e.target.dataFieldName;
    let dataFieldName;
    if (prepopulatedFieldName) {
      dataFieldName = prepopulatedFieldName;
    } else {
      dataFieldName = e.target.getAttribute('data-field-name');
    }

    let fieldValidity;
    if (dataFieldName === 'cvv') {
      fieldValidity = valid.cvv(e.target.value, 4);
    } else {
      fieldValidity = (valid[dataFieldName] ? valid[dataFieldName](e.target.value) : null);
    }
    if (!fieldValidity) { return; }

    const cardValues = this.state.cardValues;
    cardValues[dataFieldName] = e.target.value;

    // if the field was only filled in by the existing card, don't dirty the form
    // email isn't part of braintree fields so we don't need treat that
    if (!prepopulatedFieldName) {
      this.setState({ cardValues, dirty: true });
    }

    Object.assign(fieldValidity, {
      isEmpty: !e.target.value.length,
      target: {
        fieldKey: dataFieldName,
      },
    });
    this.props.braintreeFieldStateChange({ event: fieldValidity });
  }

  initializeBraintree(braintree) {
    const { braintreeToken } = this.props;
    braintree.client.create({
      authorization: braintreeToken,
    }, (clientErr, clientInstance) => {
      if (clientErr) {
        // TODO this might have to go in callback for subclient
        console.warn(JSON.stringify(clientErr));
        return;
      }
      this.client = clientInstance;
    });
  }

  dismissPage() {
    if (!this.state.dirty || window.confirm('Are you sure you want to dismiss your changes?')) {
      this.props.dismissPage();
    }
  }

  render() {
    const {
      paymentMethod,
      requiredFields,
      requiredFieldErrors,
      shouldScrollToError,
      saveBusinessProfile,
      setShouldScrollToError,
      modifyRequiredFieldList,
      submitting,
      brand,
      app,
      trackEvent,
    } = this.props;

    const existingPaymentMethod = this.getExistingPaymentMethod(this.props);

    return (
      <div>
        <AccountHeadline brand={brand} headline="MANAGE PAYMENT METHODS" app={app} trackEvent={trackEvent} />
        <div className="heavy-shadowed area dismissable">
          <div className="dismiss-button clickable" onClick={this.dismissPage} />
          <div className="row">
            <div className="col-md-11 hidden-lte-sm">
              <BusinessProfileTimeline step={1} type="edit" />
              <hr className="hidden-md basic-grey-divider timeline-divider" />
              <h3 className="h3-sg text-color-dark-slate text-weight-medium numbered-header numbered-header-1">Edit Business Profile</h3>
            </div>
          </div>
          <div className="row">
            <div className="col-xs-12 col-md-10 col-md-offset-1 padding-0">
              <h3 className="h3-sg text-color-dark-slate text-weight-medium numbered-header numbered-header-1 visible-lte-sm">Edit Business Profile</h3>
              <h4 className="h4-sg text-color-dark-slate text-weight-medium with-bottom-margin-2x">EDIT EMAIL FOR RECEIPTS AND PASSES</h4>
              <div className="row">
                <EmailInput existingPaymentMethod={existingPaymentMethod} onChange={this.handleEmailFieldChange} />
              </div>
              <h4 className="h4-sg text-color-dark-slate text-weight-medium with-bottom-margin-2x">EDIT BUSINESS PAYMENT METHOD</h4>
              <div className="col-xs-12 col-md-10 padding-0">
                <form onSubmit={this.onSubmit}>
                  <PaymentMethodForm
                    placeholdersForEdit
                    existingPaymentMethod={existingPaymentMethod}
                    newPaymentMethod={paymentMethod || new NewPaymentMethodModel()}
                    isPaypal={false}
                    creditCardFieldsClassNames="non-paypal"
                    onFieldEvent={this.onFieldEvent}
                    requiredFields={requiredFields}
                    requiredFieldErrors={requiredFieldErrors}
                    shouldScrollToError={shouldScrollToError}
                    setShouldScrollToError={setShouldScrollToError}
                    modifyRequiredFieldList={modifyRequiredFieldList}
                    saveCreditCard
                    hideSaveToggle
                    showPaypal={false}
                  />
                  <div className="row">
                    <div className="col-md-6">
                      <ProgressButton
                        buttonClassName={'btn-block btn-primary text-center progress-btn-background-green'}
                        loading={submitting}
                        onClick={this.onSubmit}
                      >
                        <input type="submit" value="SAVE AND CONTINUE" />
                      </ProgressButton>
                    </div>
                    <div className="col-md-2 text-center">
                      <a
                        className="btn-link btn-block text-weight-medium button-centered-link"
                        onClick={this.dismissPage}
                      >
                        CANCEL
                      </a>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

EditPaymentMethod.propTypes = propTypes;
EditPaymentMethod.defaultProps = defaultProps;

const dismissPage = () => push('/account/payment-methods/');

const editPaymentMethod = paymentMethod => push(paymentMethod.editURL);

const mapStateToProps = (state) => {
  const {
    paymentMethods,
    selectedPaymentMethodId,
    defaultPaymentMethodId,
    paymentMethod,
    braintreeToken,
    requiredFields,
    requiredFieldErrors,
    shouldScrollToError,
    submitting,
  } = state.paymentMethods;
  const { user } = state.account;
  const { name: app } = state.app;

  return {
    paymentMethods,
    selectedPaymentMethodId,
    defaultPaymentMethodId,
    paymentMethod,
    braintreeToken,
    requiredFields,
    requiredFieldErrors,
    shouldScrollToError,
    submitting,
    user,
    app,
  };
};

const mapDispatchToProps = dispatch => (
  bindActionCreators({
    getPaymentMethods,
    setDefaultPaymentMethod,
    deletePaymentMethod,
    editPaymentMethod,
    dismissPage,
    modifyRequiredFieldList: modifyRequiredFieldListCreator,
    setShouldScrollToError: setShouldScrollToErrorCreator,
    braintreeFieldStateChange,
    saveBusinessProfile: saveBusinessProfileCreator,
    trackEvent: trackEventCreator,
  }, dispatch)
);

export default connect(mapStateToProps, mapDispatchToProps)(EditPaymentMethod);
